import {
  DiscountFormV2StateType,
  CustomerEligibilityFormV2Type,
  CustomerEligibilityV2Type,
  DiscountStackMode,
  DiscountStackabilityFormV2Type,
  UsageLimitFormV2Type,
  AvailabilityFormV2Type,
  DiscountBackendDataV2,
  DiscountMethod,
  DiscountApplicationMethodFormV2Type,
  DiscountPreviewV2Type,
  CouponType,
  bxgyProductStrategy,
  generationSourceType,
  AmountType,
  uiConfigType,
  UiConfigDiscountType,
  DiscountApplyToType,
  EntitledCatalogueType,
  PrerequisiteCatalogueType,
  EntitledCatalogueEntityType,
  PrerequisiteCatalogueEntityType,
  ApplicationTarget,
  PurchaseConditionType,
  PurchaseConditionAmountType,
  StandardPrerequisite,
  PrerequisiteType,
  EntitledCatalogueSelectionMode
} from 'src/lib/types/discountsV2'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import { initialPreview } from './constants/discountConstants'
import { UserType } from 'src/lib/types/user'
import { permissionType } from '../constants'

export const parseUiConfigType = (
  data: DiscountBackendDataV2,
  canAccess: (value: string, quantity: number) => boolean
): uiConfigType => {
  if (data?.coupon_type === CouponType.BUNDLE) {
    return {
      type: UiConfigDiscountType.BUNDLE
    }
  }

  if (data?.coupon_type === CouponType.SHIPPING) {
    return {
      type: UiConfigDiscountType.SHIPPING
    }
  }

  if (data?.coupon_type === CouponType.BXGY) {
    if (data?.amount_type === AmountType.PERCENTAGE && data?.concession_amount === 100) {
      const countOfProducts =
        data?.metadata?.entitledCatalogue?.reduce((acc, group) => {
          return (
            acc +
            group.reduce((groupAcc, item) => {
              if (!item?.isVariant) {
                return groupAcc + (item?.variants?.length || 0)
              } else {
                return groupAcc + 1
              }
            }, 0)
          )
        }, 0) || 0

      // If collection then FREEBIE_SELECTOR
      if (
        data?.entitled_entity_quantity_distribution?.[0]?.entities?.[0]?.type ===
          EntitledCatalogueEntityType.COLLECTION &&
        canAccess(permissionType.DISCOUNTED_PRODUCT_SELECTOR, 0)
      ) {
        return {
          type: UiConfigDiscountType.FREEBIE_SELECTOR
        }
      }

      // Count of products > entitled product count then FREEBIE_SELECTOR
      if (countOfProducts > data?.entitled_product_count && canAccess(permissionType.DISCOUNTED_PRODUCT_SELECTOR, 0)) {
        return {
          type: UiConfigDiscountType.FREEBIE_SELECTOR
        }
      }

      return {
        type: UiConfigDiscountType.FREEBIE
      }
    }

    return {
      type: UiConfigDiscountType.BXGY_AUTO_ADDED
    }
  }

  if (data?.coupon_type === CouponType.EXIT_COUPON) {
    return {
      type: UiConfigDiscountType.EXIT_COUPON
    }
  }

  if (data?.coupon_type === CouponType.AMOUNT) {
    if (
      data?.prerequisite_entity_quantity_distribution?.length > 0 &&
      data?.entitled_entity_quantity_distribution?.length > 0 &&
      data?.entitled_entity_quantity_distribution?.[0]?.quantity > 0
    ) {
      return {
        type: UiConfigDiscountType.BXGY
      }
    }

    // The backend drops entitled_entity_quantity_distribution entirely for "all products/
    // collections" discounts, so an "all products with exclusions" coupon looks identical to a
    // plain one unless we also check for an all-products/collections marker or exclusions.
    const hasAllProductsOrCollectionsMarker = hasAllEntitiesMarker(data)

    const hasExclusions = data?.excluded_entity_ids?.length > 0 || data?.metadata?.excludedCatalogue?.length > 0

    if (data?.entitled_entity_quantity_distribution?.length > 0 || hasAllProductsOrCollectionsMarker || hasExclusions) {
      return {
        type: UiConfigDiscountType.AMOUNT_OFF_PRODUCTS
      }
    }

    if (
      data?.amount_type === AmountType.PERCENTAGE &&
      data?.maximum_concession_amount &&
      !data?.entitled_entity_quantity_distribution?.length
    ) {
      return {
        type: UiConfigDiscountType.AMOUNT_OFF_UPTO
      }
    }

    return {
      type: UiConfigDiscountType.AMOUNT_OFF
    }
  }

  return {
    type: UiConfigDiscountType.BUNDLE
  }
}

const hasAllEntitiesMarker = (data: DiscountBackendDataV2): boolean => {
  const metadataEntitled = data?.metadata?.entitledCatalogue?.[0] || data?.metadata?.coveredCatalogue?.[0]
  return metadataEntitled?.[0]?.id === 'shopflo-all-products' || metadataEntitled?.[0]?.id === 'shopflo-all-collections'
}

const getApplyTo = (data: DiscountBackendDataV2) => {
  const metadataEntitled = data?.metadata?.entitledCatalogue?.[0] || data?.metadata?.coveredCatalogue?.[0]

  if (metadataEntitled?.[0]?.id === 'shopflo-all-products') return DiscountApplyToType.SPECIFIC_PRODUCTS
  if (metadataEntitled?.[0]?.id === 'shopflo-all-collections') return DiscountApplyToType.SPECIFIC_COLLECTIONS

  const entitledEntities = data?.entitled_entity_quantity_distribution?.[0]?.entities

  if (entitledEntities?.length > 0) {
    const type = entitledEntities[0].type
    if (type === EntitledCatalogueEntityType.COLLECTION) return DiscountApplyToType.SPECIFIC_COLLECTIONS
    if (type === EntitledCatalogueEntityType.PRODUCT || type === EntitledCatalogueEntityType.VARIANT)
      return DiscountApplyToType.SPECIFIC_PRODUCTS
  }

  return DiscountApplyToType.ALL_PRODUCTS
}

const getPurchaseConditionConfig = (data: DiscountBackendDataV2) => {
  let purchaseConditionType = PurchaseConditionType.NONE
  let purchaseConditionAmountType = PurchaseConditionAmountType.ENTIRE_CART

  if (
    data?.prerequisite_subtotal_range ||
    (data?.prerequisite_entity_quantity_distribution?.length > 0 &&
      data.prerequisite_entity_quantity_distribution?.[0]?.price)
  ) {
    purchaseConditionType = PurchaseConditionType.CART_AMOUNT
  } else if (
    data?.prerequisite_count_to_entitlement ||
    (data?.prerequisite_entity_quantity_distribution?.length > 0 &&
      data.prerequisite_entity_quantity_distribution?.[0]?.quantity)
  ) {
    purchaseConditionType = PurchaseConditionType.CART_QUANTITY
  }

  if (purchaseConditionType !== PurchaseConditionType.NONE) {
    // For "All Products/Collections" flows the entities array is empty, so the entity type
    // isn't usable — fall back to the metadata sentinel first, same trick `getApplyTo` uses.
    const metadataPrereqFirst = data?.metadata?.prerequisiteCatalogue?.[0]?.[0]
    if (metadataPrereqFirst?.id === 'shopflo-all-collections') {
      purchaseConditionAmountType = PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
    } else if (metadataPrereqFirst?.id === 'shopflo-all-products') {
      purchaseConditionAmountType = PurchaseConditionAmountType.SPECIFIC_PRODUCTS
    } else if (data?.prerequisite_entity_quantity_distribution?.length > 0) {
      const type = data?.prerequisite_entity_quantity_distribution[0].entities[0]?.type
      if (type === PrerequisiteCatalogueEntityType.COLLECTION) {
        purchaseConditionAmountType = PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
      } else {
        purchaseConditionAmountType = PurchaseConditionAmountType.SPECIFIC_PRODUCTS
      }
    }
  }
  return { purchaseConditionType, purchaseConditionAmountType }
}

const getMetadataEntitledCatalogue = (data: DiscountBackendDataV2): CatalogueDataType[][] => {
  if (data?.metadata?.entitledCatalogue && data?.metadata?.entitledCatalogue?.length > 0) {
    return data.metadata.entitledCatalogue
  }
  if (data?.metadata?.coveredCatalogue && data?.metadata?.coveredCatalogue?.length > 0) {
    return data.metadata.coveredCatalogue
  }
  return []
}

type BackendExcludedGroup = { entities?: Array<{ type?: EntitledCatalogueEntityType; entity_id?: string[] }> }

const synthesizeExcludedMetadata = (groups?: BackendExcludedGroup[]): CatalogueDataType[][] => {
  return (groups ?? []).map(group =>
    (group?.entities ?? []).map(entity => {
      const firstId = entity?.entity_id?.[0] ?? ''
      return {
        id: firstId,
        description: firstId,
        isVariant: entity?.type === EntitledCatalogueEntityType.VARIANT,
        type: entity?.type,
        isPlaceholder: true
      }
    })
  )
}

const getMetadataExcludedCatalogue = (data: DiscountBackendDataV2): CatalogueDataType[][] => {
  if (data?.metadata?.excludedCatalogue && data?.metadata?.excludedCatalogue?.length > 0) {
    return data.metadata.excludedCatalogue
  }
  // Backend has the entity data but the merchant-dashboard metadata wasn't written on save —
  // synthesize placeholder chips (using entity ids as descriptions) so the UI still renders.
  if ((data?.excluded_entity_ids?.length ?? 0) > 0) {
    return synthesizeExcludedMetadata(data?.excluded_entity_ids)
  }
  return []
}

const getMetadataPrerequisiteExcludedCatalogue = (data: DiscountBackendDataV2): CatalogueDataType[][] => {
  if (
    data?.metadata?.prerequisiteExcludedCatalogue &&
    data?.metadata?.prerequisiteExcludedCatalogue?.length > 0
  ) {
    return data.metadata.prerequisiteExcludedCatalogue
  }
  if ((data?.prerequisite_excluded_entity_ids?.length ?? 0) > 0) {
    return synthesizeExcludedMetadata(data?.prerequisite_excluded_entity_ids)
  }
  return []
}

const parsePrerequisiteExcludedCatalogue = (data: DiscountBackendDataV2): EntitledCatalogueType[] => {
  const dedicated = data?.prerequisite_excluded_entity_ids
  if (dedicated && dedicated.length > 0) {
    return dedicated.map(item => ({
      quantity: item?.quantity,
      selectionMode: EntitledCatalogueSelectionMode.ALL,
      entities: item?.entities?.map(entity => ({
        type: entity?.type,
        entityId: entity?.entity_id
      }))
    }))
  }
  // Fallback for coupons saved before the dedicated field existed — prereq exclusions were
  // appended to `excluded_entity_ids`, split them back out using metadata group counts.
  const prereqMetaCount = data?.metadata?.prerequisiteExcludedCatalogue?.length ?? 0
  if (prereqMetaCount === 0) return []
  const entitlementMetaCount = data?.metadata?.excludedCatalogue?.length ?? 0
  return parseExcludedCatalogue(data).slice(entitlementMetaCount, entitlementMetaCount + prereqMetaCount)
}

const parseEntitlementExcludedCatalogue = (data: DiscountBackendDataV2): EntitledCatalogueType[] => {
  const all = parseExcludedCatalogue(data)
  if ((data?.prerequisite_excluded_entity_ids?.length ?? 0) > 0) return all
  const prereqMetaCount = data?.metadata?.prerequisiteExcludedCatalogue?.length ?? 0
  if (prereqMetaCount === 0) return all
  const entitlementMetaCount = data?.metadata?.excludedCatalogue?.length ?? 0
  return all.slice(0, entitlementMetaCount)
}

export const parseDiscountResponseV2 = (
  data: DiscountBackendDataV2,
  user: { userData?: UserType; canAccess: (value: string, quantity: number) => boolean }
): DiscountFormV2StateType => {
  const uiConfig = parseUiConfigType(data, user.canAccess)
  const entitledCatalogue = parseEntitledCatalogue(data)
  const excludedCatalogue = parseEntitlementExcludedCatalogue(data)
  const prerequisiteExcludedCatalogue = parsePrerequisiteExcludedCatalogue(data)
  const prerequisiteExcludedMetadata = getMetadataPrerequisiteExcludedCatalogue(data)
  const prerequisiteCatalogue = parsePrerequisiteCatalogue(data)

  const commonPrerequisite = {
    customerEligibility: getCustomerEligibility(data),
    usageLimit: getUsageLimit(data),
    stackability: getStackability(data),
    availability: getAvailability(data)
  }

  const commonEntitlement = {
    entitledProductCount: data?.entitled_product_count || -1,
    concessionAmount: data?.concession_amount ?? 0,
    generationSource: data?.generation_source ?? generationSourceType.SINGLE,
    applicationTarget: data?.application_target ?? ApplicationTarget.ALL,
    applicationMethod: getApplicationMethod(data)
  }

  const configuration = {
    preview: getPreview(data),
    couponType: data?.coupon_type ?? CouponType.AMOUNT
  }


  if (data?.coupon_type === CouponType.BUNDLE) {
    return {
      uiConfig,
      configuration,
      prerequisite: {
        ...commonPrerequisite,
        type: CouponType.BUNDLE
      },
      entitlement: {
        ...commonEntitlement,
        type: CouponType.BUNDLE,
        amountType: data?.amount_type ?? AmountType.FLAT,
        entitledCatalogue,
        excludedCatalogue,
        applyTo: getApplyTo(data),
        metaData: {
          entitledCatalogue: getMetadataEntitledCatalogue(data),
          excludedCatalogue: getMetadataExcludedCatalogue(data)
        },
        maxUsesPerOrder: Boolean(data?.multiple_application_options?.enable)
          ? data?.multiple_application_options?.number_of_times_to_apply
          : 1
      },
      errors: {}
    }
  }

  const getStandardPrerequisite = (data: DiscountBackendDataV2): StandardPrerequisite => {
    const { purchaseConditionType, purchaseConditionAmountType } = getPurchaseConditionConfig(data)

    const base = {
      ...commonPrerequisite,
      purchaseConditionType,
      purchaseConditionAmountType,
      type: data.coupon_type as CouponType.AMOUNT | CouponType.BXGY | CouponType.SHIPPING,
      excludedCatalogue: prerequisiteExcludedCatalogue
    }

    const isCartAmount = Boolean(data?.prerequisite_subtotal_range)

    const isCartQuantity = Boolean(data?.prerequisite_count_to_entitlement)

    const isSpecificResource =
      purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_PRODUCTS ||
      purchaseConditionAmountType === PurchaseConditionAmountType.SPECIFIC_COLLECTIONS

    if (isCartAmount && !isSpecificResource) {
      return {
        ...base,
        prerequisiteType: PrerequisiteType.PRICE,
        purchaseConditionType: PurchaseConditionType.CART_AMOUNT,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteSubtotalRange: data?.prerequisite_subtotal_range ?? null,
        prerequisiteMaxOrderValue: data?.prerequisite_max_order_value ?? null,
        prerequisiteCatalogue: [],
        metaData: {
          prerequisiteCatalogue: [],
          excludedCatalogue: prerequisiteExcludedMetadata
        }
      }
    }

    if (isCartQuantity && !isSpecificResource) {
      return {
        ...base,
        prerequisiteType: PrerequisiteType.QUANTITY,
        purchaseConditionType: PurchaseConditionType.CART_QUANTITY,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteCountToEntitlement: data?.prerequisite_count_to_entitlement ?? null,
        prerequisiteMaxCountForEntitlement: data?.prerequisite_max_count_for_entitlement ?? null,
        prerequisiteCatalogue: [],
        metaData: {
          prerequisiteCatalogue: [],
          excludedCatalogue: prerequisiteExcludedMetadata
        }
      }
    }

    // Check for Specific PRICE/QUANTITY via catalogue

    if (prerequisiteCatalogue.length > 0) {
      const isPrice = prerequisiteCatalogue.some(item => item.type === PrerequisiteType.PRICE)
      const isQuantity = prerequisiteCatalogue.some(item => item.type === PrerequisiteType.QUANTITY)

      if (isPrice) {
        return {
          ...base,
          prerequisiteType: PrerequisiteType.PRICE,
          purchaseConditionType: PurchaseConditionType.CART_AMOUNT,
          // Specific Amount Logic
          prerequisiteSubtotalRange: null,
          prerequisiteMaxOrderValue: null,
          prerequisiteCatalogue,
          metaData: {
            prerequisiteCatalogue: data?.metadata?.prerequisiteCatalogue ?? [],
            excludedCatalogue: prerequisiteExcludedMetadata
          }
        }
      }

      if (isQuantity) {
        return {
          ...base,
          prerequisiteType: PrerequisiteType.QUANTITY,
          purchaseConditionType: PurchaseConditionType.CART_QUANTITY,
          // Specific Quantity Logic
          prerequisiteCountToEntitlement: data?.prerequisite_count_to_entitlement ?? null,
          prerequisiteMaxCountForEntitlement: data?.prerequisite_max_count_for_entitlement ?? null,
          prerequisiteCatalogue,
          metaData: {
            prerequisiteCatalogue: data?.metadata?.prerequisiteCatalogue ?? [],
            excludedCatalogue: prerequisiteExcludedMetadata
          }
        }
      }
    }

    return {
      ...base,
      prerequisiteType: PrerequisiteType.NONE,
      purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
      prerequisiteCatalogue,
      prerequisiteCountToEntitlement: data?.prerequisite_count_to_entitlement ?? null,
      metaData: {
        prerequisiteCatalogue: data?.metadata?.prerequisiteCatalogue ?? [],
        excludedCatalogue: prerequisiteExcludedMetadata
      }
    }
  }

  const prerequisite = getStandardPrerequisite(data)

  if (data?.coupon_type === CouponType.EXIT_COUPON) {
    return {
      uiConfig,
      configuration: {
        ...configuration,
        couponType: CouponType.EXIT_COUPON
      },
      // Exit coupons share the standard purchase-condition parsing so a saved minimum cart
      // value/quantity renders back into the form, but are always modelled as an AMOUNT discount
      prerequisite: {
        ...prerequisite,
        type: CouponType.AMOUNT
      },
      entitlement: {
        ...commonEntitlement,
        applicationMethod: {
          ...commonEntitlement.applicationMethod,
          autoApplicable: true
        },
        type: CouponType.AMOUNT,
        amountType: data?.amount_type ?? AmountType.FLAT,
        maximumConcessionAmount: data?.maximum_concession_amount ?? undefined,
        entitledCatalogue: [],
        excludedCatalogue: [],
        metaData: { entitledCatalogue: [], excludedCatalogue: [] },
        applyTo: DiscountApplyToType.ALL_PRODUCTS,
        maxUsesPerOrder: data?.multiple_application_options?.number_of_times_to_apply ?? null
      },
      errors: {}
    }
  }

  if (data?.coupon_type === CouponType.BXGY) {
    return {
      uiConfig,
      configuration,
      prerequisite,
      entitlement: {
        ...commonEntitlement,
        type: CouponType.BXGY,
        amountType: data?.amount_type ?? AmountType.PERCENTAGE,
        bxgyProductStrategy: data?.bxgy_product_strategy ?? bxgyProductStrategy.ADD_PRODUCT,
        entitledCatalogue,
        excludedCatalogue,
        applyTo: getApplyTo(data),
        metaData: {
          entitledCatalogue: getMetadataEntitledCatalogue(data),
          excludedCatalogue: getMetadataExcludedCatalogue(data)
        },
        maxUsesPerOrder: data?.multiple_application_options?.number_of_times_to_apply ?? null
      },
      errors: {}
    }
  }

  if (data?.coupon_type === CouponType.SHIPPING) {
    return {
      uiConfig,
      configuration,
      prerequisite,
      entitlement: {
        ...commonEntitlement,
        type: CouponType.SHIPPING,
        amountType: data?.amount_type ?? AmountType.PERCENTAGE,
        maximumConcessionAmount: data?.maximum_concession_amount ?? undefined
      },
      errors: {}
    }
  }

  // Handle AMOUNT types
  return {
    uiConfig,
    configuration,
    prerequisite,
    entitlement: {
      ...commonEntitlement,
      type: CouponType.AMOUNT,
      amountType: data?.amount_type ?? AmountType.PERCENTAGE,
      maximumConcessionAmount: data?.maximum_concession_amount ?? undefined,
      entitledCatalogue,
      excludedCatalogue,
      applyTo: getApplyTo(data),
      metaData: {
        entitledCatalogue: getMetadataEntitledCatalogue(data),
        excludedCatalogue: getMetadataExcludedCatalogue(data)
      },
      maxUsesPerOrder: data?.multiple_application_options?.number_of_times_to_apply ?? null
    },
    errors: {}
  }
}

const parseEntitledCatalogue = (data: DiscountBackendDataV2): EntitledCatalogueType[] => {
  if (data?.entitled_entity_quantity_distribution?.length) {
    const getSelectionMode = data.entitled_entity_quantity_distribution?.[0]?.quantity
      ? EntitledCatalogueSelectionMode.ANY
      : EntitledCatalogueSelectionMode.ALL
    return data.entitled_entity_quantity_distribution.map(item => {
      return {
        quantity: item?.quantity,
        selectionMode: getSelectionMode,
        entities: item?.entities.map(entity => {
          return {
            type: entity?.type,
            entityId: entity?.entity_id
          }
        })
      }
    })
  }

  const metadataEntitled = getMetadataEntitledCatalogue(data)
  return metadataEntitled.map(() => ({
    quantity: null,
    selectionMode: EntitledCatalogueSelectionMode.ALL,
    entities: []
  }))
}

const parseExcludedCatalogue = (data: DiscountBackendDataV2): EntitledCatalogueType[] => {
  return (
    data?.excluded_entity_ids?.map(item => {
      return {
        quantity: item?.quantity,
        selectionMode: EntitledCatalogueSelectionMode.ALL,
        entities: item?.entities?.map(entity => {
          return {
            type: entity?.type,
            entityId: entity?.entity_id
          }
        })
      }
    }) ?? []
  )
}

const parsePrerequisiteCatalogue = (data: DiscountBackendDataV2): PrerequisiteCatalogueType[] => {
  return (
    data?.prerequisite_entity_quantity_distribution?.map(item => {
      const entities =
        item?.entities.map(entity => {
          return {
            type: entity?.type,
            entityId: entity?.entity_id
          }
        }) ?? []
      if (item.price) {
        return {
          type: PrerequisiteType.PRICE,
          price: item.price,
          maxPrice: item?.max_price,
          entities
        }
      }
      return {
        type: PrerequisiteType.QUANTITY,
        quantity: item?.quantity ?? 0,
        maxQuantity: item?.max_quantity,
        entities
      }
    }) ?? []
  )
}

const getPreview = (data: DiscountBackendDataV2): DiscountPreviewV2Type => {
  return {
    header: data?.header || '',
    description: data?.description || '',
    longDescription: data?.long_description?.join('\n') || ''
  }
}

const getApplicationMethod = (data: DiscountBackendDataV2): DiscountApplicationMethodFormV2Type => {
  return {
    method: data?.auto_applicable ? DiscountMethod.AUTOMATIC : DiscountMethod.CODE,
    autoApplicable: data?.auto_applicable ?? false,
    discountCode: data?.code || '',
    title: data?.title || ''
  }
}

const getCustomerEligibility = (data: DiscountBackendDataV2): CustomerEligibilityFormV2Type => {
  const cohort = data?.prerequisite_user_order_cohort || {}
  const segments = data?.segment_ids || []
  const utm = data?.utm_params || {}

  if (cohort?.lifetime_min_order_count === 0 && cohort?.lifetime_max_order_count === 0) {
    return {
      type: CustomerEligibilityV2Type.FIRST_TIME_CUSTOMERS
    }
  }

  if ((cohort?.lifetime_min_order_count ?? 0) > 0 || (cohort?.lifetime_max_order_count ?? 0) > 0) {
    return {
      type: CustomerEligibilityV2Type.LIFETIME_ORDER_COUNT,
      prerequisiteUserOrderCohort: {
        lifetimeMaxOrderCount: cohort?.lifetime_max_order_count || null,
        lifetimeMinOrderCount: cohort?.lifetime_min_order_count || 0
      }
    }
  }

  if (data?.file_meta && data?.file_meta?.download_url) {
    return {
      type: CustomerEligibilityV2Type.SPECIFIC_CUSTOMERS,
      customerSpecific: true,
      fileMeta: {
        ...data.file_meta,
        fileUrl: data?.file_meta?.file_url || '',
        fileName: data?.file_meta?.file_name || undefined,
        downloadUrl: data?.file_meta?.download_url,
        fileKey: data?.file_meta?.file_key
      }
    }
  }

  if (segments.length > 0) {
    return {
      type: CustomerEligibilityV2Type.CUSTOMER_SPECIFIC,
      segmentIds: segments,
      overridePreEvaluatedSegment: data?.override_pre_evaluated_segment ?? true
    }
  }

  if (data?.file_meta && data?.file_meta?.download_url) {
    return {
      type: CustomerEligibilityV2Type.SPECIFIC_CUSTOMERS,
      customerSpecific: true,
      fileMeta: {
        ...data.file_meta,
        fileUrl: data?.file_meta?.file_url || '',
        fileName: data?.file_meta?.file_name || undefined,
        downloadUrl: data?.file_meta?.download_url,
        fileKey: data?.file_meta?.file_key
      }
    }
  }

  if (Object.values(utm).some(v => v !== null && Array.isArray(v) && v.length > 0)) {
    return {
      type: CustomerEligibilityV2Type.UTM_PARAMS,
      utmParams: {
        source: utm?.source || [],
        medium: utm?.medium || [],
        campaign: utm?.campaign || [],
        content: utm?.content || []
      }
    }
  }

  return {
    type: CustomerEligibilityV2Type.ALL_CUSTOMERS
  }
}

const getStackability = (data: DiscountBackendDataV2): DiscountStackabilityFormV2Type => {
  const stackMode = (data.stack_config?.stack_type as DiscountStackMode) || DiscountStackMode.NONE

  switch (stackMode) {
    case DiscountStackMode.ALL:
      return {
        stackMode: DiscountStackMode.ALL
      }
    case DiscountStackMode.NONE:
      return {
        stackMode: DiscountStackMode.NONE
      }
    case DiscountStackMode.CUSTOM:
      return {
        stackMode: DiscountStackMode.CUSTOM,
        customStackableDiscountIds: data.stack_config?.includes || []
      }
    default:
      return {
        stackMode: DiscountStackMode.ALL
      }
  }
}

const getUsageLimit = (data: DiscountBackendDataV2): UsageLimitFormV2Type => {
  return {
    maxApplicableCount: data?.max_applicable_count ?? undefined,
    maxApplicablePerUserCount: data?.max_applicable_per_user_count ?? undefined
  }
}

const getAvailability = (data: DiscountBackendDataV2): AvailabilityFormV2Type => {
  return {
    enabledChannelIds: data?.enabled_channels || [],
    startDate: data?.start_date || null,
    endDate: data?.end_date || null,
    discoverable: data?.discoverable ?? true,
    active: data?.active ?? true
  }
}

export const getDiscountApplicationRules = (type: UiConfigDiscountType): Partial<DiscountFormV2StateType> => {
  const commonAvailability = {
    enabledChannelIds: [],
    startDate: Date.now(),
    endDate: null,
    discoverable: false,
    active: true
  }

  const commonStackability: DiscountStackabilityFormV2Type = {
    stackMode: DiscountStackMode.ALL
  }

  const commonUsageLimit = {
    maxApplicableCount: undefined,
    maxApplicablePerUserCount: undefined
  }

  const commonCustomerEligibility: CustomerEligibilityFormV2Type = {
    type: CustomerEligibilityV2Type.ALL_CUSTOMERS
  }

  const commonApplicationMethod = {
    method: DiscountMethod.CODE,
    autoApplicable: false,
    discountCode: '',
    title: ''
  }

  if (type === UiConfigDiscountType.BUNDLE) {
    return {
      uiConfig: {
        type: UiConfigDiscountType.BUNDLE
      },
      configuration: {
        couponType: CouponType.BUNDLE,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.BUNDLE,
        entitledCatalogue: [],
        excludedCatalogue: [],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        concessionAmount: 0,
        entitledProductCount: 1,
        amountType: AmountType.FLAT,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ALL,
        applicationMethod: commonApplicationMethod,
        applyTo: DiscountApplyToType.ALL_PRODUCTS,
        maxUsesPerOrder: null
      },
      prerequisite: {
        type: CouponType.BUNDLE,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability
      },
      errors: {}
    }
  }

  if (type === UiConfigDiscountType.BXGY) {
    return {
      uiConfig: {
        type: UiConfigDiscountType.BXGY
      },
      configuration: {
        couponType: CouponType.AMOUNT,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.AMOUNT,
        excludedCatalogue: [],
        entitledCatalogue: [
          {
            quantity: 1,
            entities: [],
            selectionMode: EntitledCatalogueSelectionMode.ANY
          }
        ],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        concessionAmount: 100,
        entitledProductCount: 0,
        amountType: AmountType.PERCENTAGE,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ALL,
        applicationMethod: commonApplicationMethod,
        applyTo: DiscountApplyToType.SPECIFIC_PRODUCTS,
        maxUsesPerOrder: null
      },
      prerequisite: {
        type: CouponType.AMOUNT,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.QUANTITY,
        purchaseConditionType: PurchaseConditionType.CART_QUANTITY,
        purchaseConditionAmountType: PurchaseConditionAmountType.SPECIFIC_PRODUCTS,
        prerequisiteCatalogue: [
          {
            type: PrerequisiteType.QUANTITY,
            quantity: 1,
            entities: []
          }
        ],
        prerequisiteCountToEntitlement: 1,
        prerequisiteMaxCountForEntitlement: 1,
        metaData: {
          prerequisiteCatalogue: []
        }
      },
      errors: {}
    }
  }

  if (type === UiConfigDiscountType.BXGY_AUTO_ADDED) {
    return {
      uiConfig: {
        type: UiConfigDiscountType.BXGY_AUTO_ADDED
      },
      configuration: {
        couponType: CouponType.BXGY,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.BXGY,
        excludedCatalogue: [],
        entitledCatalogue: [
          {
            quantity: 1,
            entities: [],
            selectionMode: EntitledCatalogueSelectionMode.ANY
          }
        ],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        concessionAmount: 100,
        entitledProductCount: 0,
        amountType: AmountType.PERCENTAGE,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ALL,
        applicationMethod: commonApplicationMethod,
        bxgyProductStrategy: bxgyProductStrategy.ADD_PRODUCT,
        applyTo: DiscountApplyToType.SPECIFIC_PRODUCTS,
        maxUsesPerOrder: null
      },
      prerequisite: {
        type: CouponType.BXGY,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.QUANTITY,
        purchaseConditionType: PurchaseConditionType.CART_QUANTITY,
        purchaseConditionAmountType: PurchaseConditionAmountType.SPECIFIC_PRODUCTS,
        prerequisiteCatalogue: [
          {
            type: PrerequisiteType.QUANTITY,
            quantity: 1,
            entities: []
          }
        ],
        prerequisiteCountToEntitlement: 1,
        prerequisiteMaxCountForEntitlement: null,
        metaData: {
          prerequisiteCatalogue: []
        }
      },
      errors: {}
    }
  }

  if (type === UiConfigDiscountType.AMOUNT_OFF) {
    return {
      errors: {},
      uiConfig: {
        type: UiConfigDiscountType.AMOUNT_OFF
      },
      configuration: {
        couponType: CouponType.AMOUNT,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.AMOUNT,
        entitledCatalogue: [],
        excludedCatalogue: [],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        concessionAmount: 0,
        entitledProductCount: 0,
        amountType: AmountType.FLAT,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ALL,
        applicationMethod: commonApplicationMethod,
        applyTo: DiscountApplyToType.ALL_PRODUCTS,
        maxUsesPerOrder: null
      },
      prerequisite: {
        type: CouponType.AMOUNT,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.NONE,
        purchaseConditionType: PurchaseConditionType.NONE,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteCatalogue: [],
        prerequisiteCountToEntitlement: null,
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    }
  }

  if (type === UiConfigDiscountType.EXIT_COUPON) {
    return {
      errors: {},
      uiConfig: {
        type: UiConfigDiscountType.EXIT_COUPON
      },
      configuration: {
        couponType: CouponType.EXIT_COUPON,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.AMOUNT,
        entitledCatalogue: [],
        excludedCatalogue: [],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        concessionAmount: 0,
        entitledProductCount: 0,
        amountType: AmountType.FLAT,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ALL,
        applicationMethod: {
          ...commonApplicationMethod,
          method: DiscountMethod.AUTOMATIC,
          autoApplicable: true
        },
        applyTo: DiscountApplyToType.ALL_PRODUCTS,
        maxUsesPerOrder: null
      },
      prerequisite: {
        type: CouponType.AMOUNT,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.NONE,
        purchaseConditionType: PurchaseConditionType.NONE,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteCatalogue: [],
        prerequisiteCountToEntitlement: null,
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    }
  }

  if (type === UiConfigDiscountType.SHIPPING) {
    return {
      errors: {},
      uiConfig: {
        type: UiConfigDiscountType.SHIPPING
      },
      configuration: {
        couponType: CouponType.SHIPPING,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.SHIPPING,
        concessionAmount: 0,
        amountType: AmountType.PERCENTAGE,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ALL,
        applicationMethod: commonApplicationMethod
      },
      prerequisite: {
        type: CouponType.SHIPPING,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.NONE,
        purchaseConditionType: PurchaseConditionType.NONE,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteCatalogue: [],
        prerequisiteCountToEntitlement: null,
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    }
  }

  if (type === UiConfigDiscountType.AMOUNT_OFF_UPTO) {
    return {
      errors: {},
      uiConfig: {
        type: UiConfigDiscountType.AMOUNT_OFF_UPTO
      },
      configuration: {
        couponType: CouponType.AMOUNT,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.AMOUNT,
        entitledCatalogue: [],
        excludedCatalogue: [],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        concessionAmount: 0,
        maximumConcessionAmount: undefined,
        entitledProductCount: 0,
        amountType: AmountType.PERCENTAGE,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ALL,
        applicationMethod: commonApplicationMethod,
        applyTo: DiscountApplyToType.ALL_PRODUCTS,
        maxUsesPerOrder: null
      },
      prerequisite: {
        type: CouponType.AMOUNT,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.NONE,
        purchaseConditionType: PurchaseConditionType.NONE,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteCatalogue: [],
        prerequisiteCountToEntitlement: null,
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    }
  }

  if (type === UiConfigDiscountType.AMOUNT_OFF_PRODUCTS) {
    return {
      errors: {},
      uiConfig: {
        type: UiConfigDiscountType.AMOUNT_OFF_PRODUCTS
      },
      configuration: {
        couponType: CouponType.AMOUNT,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.AMOUNT,
        entitledCatalogue: [
          {
            entities: [],
            selectionMode: EntitledCatalogueSelectionMode.ALL
          }
        ],
        excludedCatalogue: [],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        concessionAmount: 0,
        maximumConcessionAmount: undefined,
        entitledProductCount: 0,
        amountType: AmountType.PERCENTAGE,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ENTITLED,
        applicationMethod: commonApplicationMethod,
        applyTo: DiscountApplyToType.SPECIFIC_PRODUCTS,
        maxUsesPerOrder: null
      },
      prerequisite: {
        type: CouponType.AMOUNT,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.NONE,
        purchaseConditionType: PurchaseConditionType.NONE,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteCatalogue: [],
        prerequisiteCountToEntitlement: null,
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    }
  }

  if (type === UiConfigDiscountType.FREEBIE) {
    return {
      errors: {},
      uiConfig: {
        type: UiConfigDiscountType.FREEBIE
      },
      configuration: {
        couponType: CouponType.BXGY,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.BXGY,
        entitledCatalogue: [
          {
            quantity: 1,
            entities: [],
            selectionMode: EntitledCatalogueSelectionMode.ANY
          }
        ],
        excludedCatalogue: [],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        concessionAmount: 100,
        entitledProductCount: 0,
        amountType: AmountType.PERCENTAGE,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ENTITLED,
        applicationMethod: commonApplicationMethod,
        bxgyProductStrategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
        applyTo: DiscountApplyToType.SPECIFIC_PRODUCTS,
        maxUsesPerOrder: null
      },
      prerequisite: {
        type: CouponType.BXGY,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.NONE,
        purchaseConditionType: PurchaseConditionType.NONE,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteCatalogue: [],
        prerequisiteCountToEntitlement: 1,
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    }
  }

  if (type === UiConfigDiscountType.FREEBIE_SELECTOR) {
    return {
      errors: {},
      uiConfig: {
        type: UiConfigDiscountType.FREEBIE_SELECTOR
      },
      configuration: {
        couponType: CouponType.BXGY,
        preview: { ...initialPreview }
      },
      entitlement: {
        type: CouponType.BXGY,
        excludedCatalogue: [],
        entitledCatalogue: [
          {
            quantity: 1,
            entities: [],
            selectionMode: EntitledCatalogueSelectionMode.ANY
          }
        ],
        metaData: {
          entitledCatalogue: [],
          excludedCatalogue: []
        },
        maxUsesPerOrder: null,
        concessionAmount: 100,
        entitledProductCount: 1,
        amountType: AmountType.PERCENTAGE,
        generationSource: generationSourceType.SINGLE,
        applicationTarget: ApplicationTarget.ENTITLED,
        applicationMethod: commonApplicationMethod,
        bxgyProductStrategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
        applyTo: DiscountApplyToType.SPECIFIC_PRODUCTS
      },
      prerequisite: {
        type: CouponType.BXGY,
        customerEligibility: commonCustomerEligibility,
        usageLimit: commonUsageLimit,
        stackability: commonStackability,
        availability: commonAvailability,
        prerequisiteType: PrerequisiteType.NONE,
        purchaseConditionType: PurchaseConditionType.NONE,
        purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
        prerequisiteCatalogue: [],
        prerequisiteCountToEntitlement: 1,
        metaData: {
          prerequisiteCatalogue: []
        }
      }
    }
  }

  return {
    errors: {},
    uiConfig: {
      type: UiConfigDiscountType.AMOUNT_OFF
    },
    entitlement: {
      type: CouponType.AMOUNT,
      entitledCatalogue: [],
      excludedCatalogue: [],
      metaData: {
        entitledCatalogue: [],
        excludedCatalogue: []
      },
      concessionAmount: 0,
      entitledProductCount: 0,
      amountType: AmountType.FLAT,
      generationSource: generationSourceType.SINGLE,
      applicationTarget: ApplicationTarget.ALL,
      applicationMethod: commonApplicationMethod,
      applyTo: DiscountApplyToType.ALL_PRODUCTS,
      maxUsesPerOrder: 1
    },
    prerequisite: {
      type: CouponType.AMOUNT,
      customerEligibility: commonCustomerEligibility,
      usageLimit: commonUsageLimit,
      stackability: commonStackability,
      availability: commonAvailability,
      prerequisiteType: PrerequisiteType.NONE,
      purchaseConditionType: PurchaseConditionType.NONE,
      purchaseConditionAmountType: PurchaseConditionAmountType.ENTIRE_CART,
      prerequisiteCatalogue: [],
      prerequisiteCountToEntitlement: null,
      metaData: {
        prerequisiteCatalogue: []
      }
    }
  } as Partial<DiscountFormV2StateType>
}
